import React, { useEffect, useState, useMemo } from 'react';
import {
  Layers,
  FileText,
  Car,
  CircleAlert,
  Image as ImageIcon,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Loader2,
  Filter,
  BarChart3,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { CHAPTER_TITLES_DE } from '@shared/chapters';

// ── Types ──

interface QuestionRow {
  id: string;
  chapter_id: number;
  question_text_de: string;
  visual_prompt: string | null;
  image_url: string | null;
  difficulty: string;
  type: string;
}

type ImageCategory = 'wissen_text' | 'situation' | 'schild_identifikation';

interface CategoryInfo {
  key: ImageCategory;
  label: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  description: string;
  recommendation: string;
}

// ── Constants ──

const CATEGORIES: CategoryInfo[] = [
  {
    key: 'wissen_text',
    label: 'Wissen / Text',
    icon: <FileText size={18} />,
    color: '#6366f1',
    bg: 'rgba(99,102,241,0.1)',
    description:
      'Reine Wissensfragen ohne visuelle Komponente. Die Frage ist vollständig textbasiert beantwortbar.',
    recommendation:
      'Bild entfernen — wie im amtlichen Fragenkatalog sind diese Fragen rein textbasiert.',
  },
  {
    key: 'situation',
    label: 'Verkehrssituation',
    icon: <Car size={18} />,
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.1)',
    description:
      'Beschreibt eine Verkehrssituation aus Fahrerperspektive. Ein Szene-Bild kann den Kontext verdeutlichen.',
    recommendation: 'AI-Bild beibehalten, aber OHNE erkennbare Verkehrsschilder generieren.',
  },
  {
    key: 'schild_identifikation',
    label: 'Schild-Identifikation',
    icon: <CircleAlert size={18} />,
    color: '#ef4444',
    bg: 'rgba(239,68,68,0.1)',
    description:
      'Die Frage bezieht sich direkt auf ein konkretes Verkehrszeichen. Das Schild muss exakt korrekt sein.',
    recommendation:
      'Offizielle StVO-SVG statt AI-Bild verwenden — AI kann Schilder nicht korrekt zeichnen.',
  },
];

const CHAPTER_NAMES = CHAPTER_TITLES_DE;

// ── Categorization Logic ──

function categorizeQuestion(text: string): ImageCategory {
  const t = text.toLowerCase();

  // Category: Schild-Identifikation
  if (
    /was bedeutet (dieses|das|folgendes) (verkehrs)?zeichen/.test(t) ||
    /was zeigt (dieses|das) (verkehrs)?zeichen/.test(t) ||
    /welches (verkehrs)?zeichen/.test(t) ||
    /dieses schild/.test(t) ||
    /dieses verkehrszeichen/.test(t) ||
    /folgendes zeichen/.test(t) ||
    /was bedeutet ein (rundes|dreieckiges|achteckiges|blaues|rotes).*schild/.test(t) ||
    /zeichen \d{3}/.test(t) ||
    /sie sehen (ein|das|dieses) (runde|dreieckige|achteckige|blaue).*schild/.test(t) ||
    /sie sehen das verkehrszeichen/.test(t)
  ) {
    return 'schild_identifikation';
  }

  // Category: Situation
  if (
    /sie (fahren|nähern|stehen|biegen|möchten|befinden|befahren|kommen)/.test(t) ||
    /sie sind (auf|an|in) /.test(t) ||
    /an (einer|der|dieser) kreuzung/.test(t) ||
    /auf (einer|der) (autobahn|landstraße|bundesstraße)/.test(t) ||
    /an (einem|dem) bahnübergang/.test(t) ||
    /in (einer|der) (kurve|baustelle|spielstraße)/.test(t) ||
    /vor ihnen/.test(t) ||
    /hinter ihnen/.test(t) ||
    /ein (fahrzeug|lkw|pkw|motorrad|radfahrer|fußgänger).*(kommt|fährt|steht)/.test(t)
  ) {
    return 'situation';
  }

  return 'wissen_text';
}

// ── Component ──

export const ImageCategoryPage: React.FC = () => {
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState<ImageCategory | ''>('');
  const [filterChapter, setFilterChapter] = useState<number | ''>('');
  const [expandedCategory, setExpandedCategory] = useState<ImageCategory | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'overview' | 'list'>('overview');

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('questions')
      .select('id, chapter_id, question_text_de, visual_prompt, image_url, difficulty, type')
      .not('question_text_de', 'is', null)
      .order('chapter_id');
    setQuestions(data || []);
    setLoading(false);
  };

  // Categorize all questions
  const categorized = useMemo(() => {
    const result: Record<ImageCategory, QuestionRow[]> = {
      wissen_text: [],
      situation: [],
      schild_identifikation: [],
    };
    for (const q of questions) {
      const cat = categorizeQuestion(q.question_text_de || '');
      result[cat].push(q);
    }
    return result;
  }, [questions]);

  // Stats per chapter per category
  const chapterStats = useMemo(() => {
    const stats: Record<number, Record<ImageCategory, number>> = {};
    for (const q of questions) {
      const ch = q.chapter_id;
      const cat = categorizeQuestion(q.question_text_de || '');
      if (!stats[ch]) stats[ch] = { wissen_text: 0, situation: 0, schild_identifikation: 0 };
      stats[ch][cat]++;
    }
    return stats;
  }, [questions]);

  // Filtered questions for list view
  const filteredQuestions = useMemo(() => {
    let list = questions.map((q) => ({
      ...q,
      category: categorizeQuestion(q.question_text_de || ''),
    }));
    if (filterCategory) list = list.filter((q) => q.category === filterCategory);
    if (filterChapter) list = list.filter((q) => q.chapter_id === filterChapter);
    return list;
  }, [questions, filterCategory, filterChapter]);

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>
            <Layers size={24} /> Bild-Kategorien
          </h1>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Loader2
            size={32}
            style={{ animation: 'spin 1s linear infinite', color: 'var(--primary)' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <Layers size={24} /> Bild-Kategorien
        </h1>
        <div className="flex gap-2">
          <button
            className={`btn btn-sm ${viewMode === 'overview' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('overview')}
          >
            <BarChart3 size={14} /> Übersicht
          </button>
          <button
            className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('list')}
          >
            <Filter size={14} /> Einzelansicht
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid-cols-4 mb-6" style={{ gap: 16 }}>
        <div className="kpi-card">
          <span className="kpi-label">Gesamt</span>
          <span className="kpi-value">{questions.length}</span>
          <span className="text-xs text-muted">Fragen</span>
        </div>
        {CATEGORIES.map((cat) => (
          <div
            key={cat.key}
            className="kpi-card"
            style={{
              cursor: 'pointer',
              border: filterCategory === cat.key ? `2px solid ${cat.color}` : undefined,
              transition: 'border-color 0.2s',
            }}
            onClick={() => {
              setFilterCategory(filterCategory === cat.key ? '' : cat.key);
              if (viewMode === 'overview') setViewMode('list');
            }}
          >
            <span className="kpi-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: cat.color }}>{cat.icon}</span> {cat.label}
            </span>
            <span className="kpi-value">{categorized[cat.key].length}</span>
            <span className="text-xs text-muted">
              {Math.round((categorized[cat.key].length / questions.length) * 100)}%{' · '}
              {categorized[cat.key].filter((q) => q.image_url).length} mit Bild
            </span>
          </div>
        ))}
      </div>

      {viewMode === 'overview' ? (
        <>
          {/* ── Category Cards ── */}
          <div className="flex flex-col gap-4 mb-6">
            {CATEGORIES.map((cat) => {
              const items = categorized[cat.key];
              const isExpanded = expandedCategory === cat.key;
              const withImage = items.filter((q) => q.image_url).length;

              return (
                <div key={cat.key} className="card" style={{ border: `1px solid ${cat.color}22` }}>
                  <div
                    className="flex items-center justify-between"
                    style={{ cursor: 'pointer', padding: '4px 0' }}
                    onClick={() => setExpandedCategory(isExpanded ? null : cat.key)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: cat.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: cat.color,
                        }}
                      >
                        {cat.icon}
                      </div>
                      <div>
                        <h3 style={{ margin: 0 }}>{cat.label}</h3>
                        <p className="text-sm text-muted" style={{ margin: 0 }}>
                          {cat.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: cat.color }}>
                          {items.length}
                        </div>
                        <div className="text-xs text-muted">{withImage} mit Bild</div>
                      </div>
                      {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </div>
                  </div>

                  {/* Recommendation */}
                  <div
                    style={{
                      marginTop: 12,
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: cat.bg,
                      fontSize: 13,
                    }}
                  >
                    <strong>Empfehlung:</strong> {cat.recommendation}
                  </div>

                  {/* Expanded: show examples */}
                  {isExpanded && (
                    <div style={{ marginTop: 16 }}>
                      <div className="text-xs text-muted mb-2" style={{ fontWeight: 600 }}>
                        Beispiele ({Math.min(10, items.length)} von {items.length}):
                      </div>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {items.slice(0, 10).map((q) => (
                          <div
                            key={q.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              padding: '8px 12px',
                              borderRadius: 8,
                              background: 'var(--bg)',
                              fontSize: 13,
                            }}
                          >
                            <span
                              className="badge badge-neutral"
                              style={{ minWidth: 28, textAlign: 'center' }}
                            >
                              {q.chapter_id}
                            </span>
                            <span
                              style={{
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {q.question_text_de}
                            </span>
                            {q.image_url ? (
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewImage(q.image_url);
                                }}
                                title="Bild ansehen"
                              >
                                <Eye size={14} />
                              </button>
                            ) : (
                              <span className="text-xs text-muted">
                                <EyeOff size={14} />
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {items.length > 10 && (
                        <button
                          className="btn btn-ghost btn-sm mt-2"
                          onClick={() => {
                            setFilterCategory(cat.key);
                            setViewMode('list');
                          }}
                        >
                          Alle {items.length} anzeigen →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Chapter Breakdown Table ── */}
          <div className="card">
            <h3 style={{ marginBottom: 12 }}>Aufschlüsselung nach Kapitel</h3>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Kapitel</th>
                    <th style={{ textAlign: 'center' }}>📝 Wissen</th>
                    <th style={{ textAlign: 'center' }}>🚗 Situation</th>
                    <th style={{ textAlign: 'center' }}>🔵 Schild</th>
                    <th style={{ textAlign: 'center' }}>Gesamt</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(chapterStats)
                    .sort((a, b) => Number(a) - Number(b))
                    .map((ch) => {
                      const s = chapterStats[Number(ch)];
                      const total = s.wissen_text + s.situation + s.schild_identifikation;
                      return (
                        <tr key={ch}>
                          <td>
                            <span className="badge badge-neutral" style={{ marginRight: 8 }}>
                              {ch}
                            </span>
                            {CHAPTER_NAMES[Number(ch)] || `Kapitel ${ch}`}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                color:
                                  s.wissen_text > 0 ? CATEGORIES[0].color : 'var(--text-muted)',
                              }}
                            >
                              {s.wissen_text}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                color: s.situation > 0 ? CATEGORIES[1].color : 'var(--text-muted)',
                              }}
                            >
                              {s.situation}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                color:
                                  s.schild_identifikation > 0
                                    ? CATEGORIES[2].color
                                    : 'var(--text-muted)',
                              }}
                            >
                              {s.schild_identifikation}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 600 }}>{total}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Visual Distribution Bar ── */}
          <div className="card mt-4">
            <h3 style={{ marginBottom: 12 }}>Verteilung</h3>
            <div
              style={{
                display: 'flex',
                height: 32,
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid var(--border)',
              }}
            >
              {CATEGORIES.map((cat) => {
                const pct = (categorized[cat.key].length / questions.length) * 100;
                return (
                  <div
                    key={cat.key}
                    style={{
                      width: `${pct}%`,
                      background: cat.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'opacity 0.2s',
                    }}
                    title={`${cat.label}: ${categorized[cat.key].length} (${Math.round(pct)}%)`}
                    onClick={() => {
                      setFilterCategory(cat.key);
                      setViewMode('list');
                    }}
                  >
                    {pct > 8 ? `${Math.round(pct)}%` : ''}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 mt-2">
              {CATEGORIES.map((cat) => (
                <div key={cat.key} className="flex items-center gap-2 text-xs">
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: cat.color,
                      display: 'inline-block',
                    }}
                  />
                  {cat.label} ({categorized[cat.key].length})
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        /* ── List View ── */
        <div className="card">
          <div className="flex items-center gap-3 mb-4" style={{ flexWrap: 'wrap' }}>
            <select
              className="input"
              style={{ width: 180 }}
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as ImageCategory | '')}
            >
              <option value="">Alle Kategorien</option>
              {CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label} ({categorized[c.key].length})
                </option>
              ))}
            </select>
            <select
              className="input"
              style={{ width: 180 }}
              value={filterChapter}
              onChange={(e) => setFilterChapter(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Alle Kapitel</option>
              {Object.entries(CHAPTER_NAMES).map(([k, v]) => (
                <option key={k} value={k}>
                  Kap. {k}: {v}
                </option>
              ))}
            </select>
            <span className="text-sm text-muted">{filteredQuestions.length} Fragen</span>
          </div>

          <div style={{ maxHeight: 600, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Kap.</th>
                  <th style={{ width: 130 }}>Kategorie</th>
                  <th>Fragetext</th>
                  <th style={{ width: 60 }}>Bild</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuestions.slice(0, 100).map((q) => {
                  const catInfo = CATEGORIES.find((c) => c.key === q.category)!;
                  return (
                    <tr key={q.id}>
                      <td>
                        <span className="badge badge-neutral">{q.chapter_id}</span>
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: catInfo.bg,
                            color: catInfo.color,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {catInfo.icon} {catInfo.label}
                        </span>
                      </td>
                      <td>
                        <div
                          style={{
                            maxWidth: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={q.question_text_de}
                        >
                          {q.question_text_de}
                        </div>
                      </td>
                      <td>
                        {q.image_url ? (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setPreviewImage(q.image_url)}
                            title="Bild ansehen"
                          >
                            <ImageIcon size={14} style={{ color: 'var(--primary)' }} />
                          </button>
                        ) : (
                          <span className="text-xs text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredQuestions.length > 100 && (
              <div className="text-sm text-muted" style={{ padding: 12, textAlign: 'center' }}>
                Zeigt 100 von {filteredQuestions.length} Fragen
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Image Preview Modal ── */}
      {previewImage && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setPreviewImage(null)}
        >
          <img
            src={previewImage}
            alt="Vorschau"
            style={{
              maxWidth: '80vw',
              maxHeight: '80vh',
              borderRadius: 12,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          />
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
